import axios, { AxiosResponse } from "axios";
import Cookies from "js-cookie";
declare const BASEPATH: string;
declare const USERSERVICE: string;
declare const APP_NAME: string;
const WEBSOCKET_PORT = 6001,
  WEBSOCKET_KEY = "websocket_key";
import RoleApi from "@/API/Role/Role";
import { pro } from "@he-tree/vue";
const roleApi = new RoleApi();

export default class Auth {
  static USER: Record<string, any> = {};
  static AUTHORIZED: boolean = false;
  static SESSON: Record<string, any> = {};
  static ROLE: Record<string, any> = {};
  static logoutTimer: ReturnType<typeof setTimeout> | null = null;
  static isCheckingAuth: boolean = false; // Prevent multiple simultaneous auth checks

  static LANGUAGE = [
    { name: "English", code: "en" },
    { name: "Arabic", code: "ar" },
  ];
  
  static SETTINGS = {
    user: {},
    system: {
      comment_approve: true,
    },
  };
  
  static PERMISSIONS: string[] = [];
  static TABLE_PERMISSIONS = {};

  // static baseURL: string = BASEPATH;
  static baseURL: string = USERSERVICE;

  static run() {
    axios.defaults.baseURL = this.baseURL;
    // Set up axios interceptors for better error handling
    this.setupAxiosInterceptors();
  }

  /**
   * Setup axios interceptors for automatic token refresh and error handling
   */
  static setupAxiosInterceptors() {
    // Request interceptor to add token to requests
    axios.interceptors.request.use(
      (config) => {
        const token = this.getStoredToken();
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Response interceptor for handling 401 errors
    axios.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401 && this.AUTHORIZED) {
          console.log('Token expired, logging out...');
          this.logOut();
        }
        return Promise.reject(error);
      }
    );
  }

  /**
   * Get stored token from cookies
   */
  static getStoredToken(): string {
    let fullToken = "";
    let i = 1;
    console.log(APP_NAME);
    while (Cookies.get(`${APP_NAME}_api_token_part${i}`)) {
      fullToken += Cookies.get(`${APP_NAME}_api_token_part${i}`);
      i++;
    }
    return fullToken;
  }

  /**
   * Store token in cookies with chunking
   */
  static storeToken(token: string, remember_me: boolean = false): void {
    // Clear existing token parts first
    this.clearStoredToken();
    
    const partSize = 3000;
    const totalParts = Math.ceil(token.length / partSize);
    const expires = remember_me ? 30 : 1; // 30 days if remember me, 1 day otherwise

    for (let i = 0; i < totalParts; i++) {
      const part = token.substring(i * partSize, (i + 1) * partSize);
      Cookies.set(`${APP_NAME}_api_token_part${i + 1}`, part, { expires });
    }
  }

  /**
   * Clear stored token from cookies
   */
  static clearStoredToken(): void {
    let i = 1;
    while (Cookies.get(`${APP_NAME}_api_token_part${i}`)) {
      Cookies.remove(`${APP_NAME}_api_token_part${i}`);
      i++;
    }
  }

  /**
   * Login user with credentials
   */
  static async logIn(
    login: string,
    password: string,
    remember_me = false
  ): Promise<boolean> {
    try {
      axios.defaults.baseURL = USERSERVICE;
      const response: AxiosResponse = await axios.post("login", {
        login,
        password,
        remember_me,
      });

      const token = response.data.data.token;
       if (response.data.data.department && response.data.data.job) {
          response.data.data.is_manager =
            response.data.data?.department?.manager_id ==
            response.data.data?.job?.position_id;
        }

      // Store token in cookies
      this.storeToken(token, remember_me);
      
      // Set axios default header
      axios.defaults.headers.common.Authorization = `Bearer ${token}`;

      // Set user data
      await this.setData(response);

      // Setup auto logout timer (1 hour)
      this.setupLogoutTimer();

      return true;
    } catch (error: any) {
      console.error('Login error:', error);
      if (error.response) {
        const { status, data } = error.response;
        throw new Error(data.errors?.[0]?.[0] || data.message || "Login failed");
      } else {
        throw new Error("An unexpected error occurred during login.");
      }
    }
  }

  /**
   * Check if user is logged in
   */
  static async loggedIn(): Promise<boolean> {
    // If already authorized and user data exists, return immediately
    if (this.AUTHORIZED && this.USER && Object.keys(this.USER).length > 0) {
      return true;
    }

    // Prevent multiple simultaneous auth checks
    if (this.isCheckingAuth) {
      // Wait for ongoing check to complete
      while (this.isCheckingAuth) {
        await new Promise(resolve => setTimeout(resolve, 50));
      }
      return this.AUTHORIZED;
    }

    this.isCheckingAuth = true;

    try {
      axios.defaults.baseURL = this.baseURL;
      
      const fullToken = this.getStoredToken();
      
      if (!fullToken) {
        this.isCheckingAuth = false;
        this.AUTHORIZED = false;
        return false;
      }

      // Check if token is already set in axios headers
      if (!axios.defaults.headers.common.Authorization) {
        axios.defaults.headers.common.Authorization = `Bearer ${fullToken}`;
      }

      // Only fetch user info if we don't have it
      if (!this.USER || Object.keys(this.USER).length === 0) {
        const params = {
          "with[]": ["department", "role", "job", "manager", "teams"],
        };
        
        const response: AxiosResponse = await axios.get("user-info", { params });
        await this.setData(response);
        if (response.data.data.department && response.data.data.job) {
          response.data.data.is_manager =
            response.data.data?.department?.manager_id ==
            response.data.data?.job?.position_id;
        }
      }
      this.setupLogoutTimer();
      this.isCheckingAuth = false;
      return true;
      
    } catch (error) {
      console.error('Auth check failed:', error);
      this.AUTHORIZED = false;
      this.USER = {};
      this.clearStoredToken();
      this.isCheckingAuth = false;
      return false;
    }
  }
  /**
   * Set user data from response
   */
  static async setData(response: AxiosResponse): Promise<void> {
    try {
      this.PERMISSIONS = response.data.data.permissions || [];
      this.AUTHORIZED = true;
      this.USER = response.data.data;
      
      // Get role data if user has job_id and department_id
      if (this.USER.job_id && this.USER.department_id) {
        try {
          this.ROLE = await roleApi.getRoleJobWithDepartment(
            this.USER.job_id,
            this.USER.department_id
          );
        } catch (roleError) {
          console.warn('Failed to fetch role data:', roleError);
          this.ROLE = {};
        }
      }
    } catch (error) {
      console.error('Error setting user data:', error);
      throw error;
    }
  }

  /**
   * Setup automatic logout timer
   */
  static setupLogoutTimer(): void {
    // Clear existing timer
    if (this.logoutTimer) {
      clearTimeout(this.logoutTimer);
    }

    // Set new timer for 8 hours
    const tokenDuration = 60 * 60 * 8 * 1000; // 8 hours in milliseconds
    this.logoutTimer = setTimeout(() => {
      console.log("Token expired, logging out automatically...");
      this.logOut();
    }, tokenDuration);
  }

  /**
   * Logout user
   */
  static logOut(): boolean {
    try {
      // Clear stored tokens
      this.clearStoredToken();

      // Clear logout timer
      if (this.logoutTimer) {
        clearTimeout(this.logoutTimer);
        this.logoutTimer = null;
      }

      // Reset auth state
      this.AUTHORIZED = false;
      this.USER = {};
      this.ROLE = {};
      this.PERMISSIONS = [];
      this.isCheckingAuth = false;

      // Clear axios auth header
      delete axios.defaults.headers.common.Authorization;

      // Clear local storage
      localStorage.removeItem('navActiveState');
      localStorage.removeItem('selectedLanguageCode');

      // Redirect to login
      window.location.href = "/login";
      
      return true;
    } catch (error) {
      console.error('Error during logout:', error);
      return false;
    }
  }

  /**
   * Get current auth token
   */
  static async getAuthToken(): Promise<string | null> {
    try {
      return this.getStoredToken() || null;
    } catch (error) {
      console.error("Error getting auth token:", error);
      return null;
    }
  }

  /**
   * Check if user has specific permission
   */
  static hasPermission(permission: string): boolean {
    return this.PERMISSIONS.includes(permission);
  }

  /**
   * Check if user is super admin
   */
  static isSuperAdmin(): boolean {
    return this.USER?.super === true || this.USER?.super == 'true';
  }

  /**
   * Get user info
   */
  static getUser(): Record<string, any> {
    return this.USER;
  }

  /**
   * Get user role
   */
  static getRole(): Record<string, any> {
    return this.ROLE;
  }

  /**
   * Get user permissions
   */
  static getPermissions(): string[] {
    return this.PERMISSIONS;
  }

  /**
   * Refresh user data
   */
  static async refreshUserData(): Promise<boolean> {
    try {
      const params = {
        "with[]": ["department", "role", "job", "manager", "teams"],
      };
      
      const response: AxiosResponse = await axios.get("user-info", { params });
      if (response.data.data.department && response.data.data.job) {
        response.data.data.is_manager =
          response.data.data?.department?.manager_id ==
          response.data.data?.job?.position_id;
      }
      await this.setData(response);
      return true;
    } catch (error) {
      console.error('Failed to refresh user data:', error);
      return false;
    }
  }

  /**
   * Setup WebSocket connection (placeholder for future implementation)
   */
  static setupWebSocketConnection(): void {
    // WebSocket implementation would go here
    // Currently commented out in the original code
  }
}